import useWebSocketDataStore from '../Store/WebSocketDataStore';
import usePlaybackStore from '../Store/playbackStore';
import {
  SERIES_MINUTE_MS,
  denseSeriesGapRanges,
  forwardFillSeriesAtGaps,
  mergeSeriesGapRanges,
  replaySeriesGapRanges,
  seriesGapRangesForDate,
} from '../Utils/seriesPresentation';

// Define available symbols and their metadata
const AVAILABLE_SYMBOLS = {
  'SPX': {
    symbol: 'SPX',
    full_name: 'S&P 500',
    description: 'S&P 500 Index',
    type: 'index',
    exchange: 'INDEX',
    minmov: 1,
    pricescale: 1,
  },
  'ES': {
    symbol: 'ES',
    full_name: 'E-mini S&P 500 Futures',
    description: 'E-mini S&P 500 Futures',
    type: 'futures',
    exchange: 'CME',
    minmov: 25,
    pricescale: 100,
  },
};

class CustomDataFeed {
  constructor() {
    this.subscribers = {};
    this.storeUnsubscribers = {};
    this.playbackUnsubscribers = {};
    this.lastBars = {};
    this.destroyed = false;
    // console.log('[CustomDataFeed] Initialized.');
  }

  onReady(callback) {
    // console.log('[CustomDataFeed] onReady called.');
    const configuration = {
      supported_resolutions: ['1'],
      supports_search: true,              // Enable symbol search
      supports_group_request: false,
      supports_marks: false,
      supports_timescale_marks: false,
      supports_time: true,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,

      // timezone: "America/New_York",
      // timezone: "UTC",
      exchanges: [
        {value: "INDEX", name: "US Indices", desc: "US Major Indices"},
        {value: "CME", name: "CME", desc: "CME Futures"}
      ],
      symbols_types: [
        {name: "Index", value: "index"},
        {name: "Futures", value: "futures"}
      ]
    };
    setTimeout(() => callback(configuration), 0);
  }

  searchSymbols(userInput, exchange, symbolType, onResultReadyCallback) {
    // console.log('[CustomDataFeed] searchSymbols:', userInput, exchange, symbolType);

    const searchInput = userInput.toUpperCase();
    const results = Object.values(AVAILABLE_SYMBOLS)
        .filter(sym =>
            sym.symbol.includes(searchInput) ||
            sym.full_name.toUpperCase().includes(searchInput)
        )
        .map(sym => ({
          symbol: sym.symbol,
          full_name: sym.full_name,
          description: sym.description,
          exchange: sym.exchange,
          type: sym.type,
          ticker: sym.symbol
        }));

    onResultReadyCallback(results);
  }

  resolveSymbol(symbolName, onSymbolResolvedCallback, onResolveErrorCallback) {
    // console.log(`[CustomDataFeed] Resolving symbol: ${symbolName}`);

    const cleanSymbol = symbolName.replace('I:', '');
    const symbolData = AVAILABLE_SYMBOLS[cleanSymbol];

    if (!symbolData) {
      // console.error(`[CustomDataFeed] Symbol not found: ${cleanSymbol}`);
      onResolveErrorCallback('unknown_symbol');
      return;
    }

    const symbolInfo = {
      name: symbolName,
      ticker: symbolName,
      type: symbolData.type,
      session: '24x7',
      timezone: "America/New_York",
      minmov: symbolData.minmov,
      pricescale: symbolData.pricescale,
      has_intraday: true,
      has_daily: false,
      supported_resolutions: ['1', '3', '5', '10'],
      exchange: symbolData.exchange,
      format: 'price',
      description: symbolData.description,
      full_name: symbolData.full_name
    };

    setTimeout(() => {
      // console.log(`[CustomDataFeed] Symbol resolved:`, symbolInfo);
      onSymbolResolvedCallback(symbolInfo);
    }, 0);
  }


  // // Existing getBars method remains unchanged this is for single day data.

  getBars(symbolInfo, resolution, {from, to, countBack}, onHistoryCallback, onErrorCallback) {
    try {
      const websocketState = useWebSocketDataStore.getState();
      const {indexData} = websocketState;
      const cleanSymbol = symbolInfo.ticker.replace('I:', '');
      const data = indexData[cleanSymbol];

      if (!data || data.length === 0) {
        onHistoryCallback([], {noData: true});
        return;
      }

      const playback = usePlaybackStore.getState();
      const replayCutoffMs = playback.replayCutoffMs;
      const toMs = playback.mode === 'playing'
        && replayCutoffMs != null
        && Number.isFinite(replayCutoffMs)
        ? replayCutoffMs
        : Date.now();
      const fromMs = toMs - 7 * 24 * 60 * 60 * 1000;

      const requestedFromMs = Number(from) * 1000;
      // `to` is exclusive, but a bar stamped exactly at the replay cutoff is
      // available to the replay. Extend only the availability cap by 1ms so
      // that bar remains eligible without admitting a later bar.
      const requestedToMs = Math.min(Number(to) * 1000, toMs + 1);
      const requestedCountBack = Number(countBack);
      const hasCountBack = Number.isInteger(requestedCountBack) && requestedCountBack > 0;
      const available = data.filter(entry => entry.time >= fromMs && entry.time <= toMs);
      const declaredGaps = playback.mode === 'playing'
        ? replaySeriesGapRanges(playback.replayCoverage, [`index:${cleanSymbol}`], replayCutoffMs)
        : seriesGapRangesForDate(websocketState.liveGapRanges, toMs);
      const gaps = mergeSeriesGapRanges(
        declaredGaps,
        denseSeriesGapRanges(available, { source: `index:${cleanSymbol}` }),
      );
      const projectedAvailable = forwardFillSeriesAtGaps(available, gaps, {
        withTime: (entry, time) => ({
          ...entry,
          time,
          open: Number(entry.close),
          high: Number(entry.close),
          low: Number(entry.close),
          close: Number(entry.close),
        }),
      });
      const inRange = projectedAvailable.filter(entry => (
        entry.time >= requestedFromMs && entry.time < requestedToMs
      ));
      const sourceBars = hasCountBack
        ? projectedAvailable.filter(entry => entry.time < requestedToMs)
        : inRange;

      const applyRequestedRange = (bars, barIntervalMs) => {
        if (!hasCountBack) return bars;
        const inRangeCount = bars.reduce((count, bar) => {
          const overlapsRequestedRange = bar.time < requestedToMs
            && bar.time + barIntervalMs > requestedFromMs;
          return overlapsRequestedRange ? count + 1 : count;
        }, 0);
        // TradingView gives the explicit range priority when it contains more
        // bars than countBack. This is how a Session request loads every bar.
        return bars.slice(-Math.max(requestedCountBack, inRangeCount));
      };

      if (sourceBars.length === 0) {
        const nextTime = available.find(entry => entry.time >= requestedToMs)?.time ?? null;
        onHistoryCallback([], { noData: true, nextTime });
        return;
      }

      if (resolution === '1') {
        const bars = sourceBars.map(entry => ({
          time: entry.time,
          open: Number(entry.open),
          high: Number(entry.high),
          low: Number(entry.low),
          close: Number(entry.close)
        }));

        const resultBars = applyRequestedRange(bars, SERIES_MINUTE_MS);
        this.lastBars[cleanSymbol] = resultBars[resultBars.length - 1];

        onHistoryCallback(resultBars, { noData: false });
        return;
      }

      // Aggregate to higher resolution
      const intervalMinutes = parseInt(resolution, 10);
      const intervalMs = intervalMinutes * 60 * 1000;

      let resultBars = [];
      let bucket = null;

      for (const entry of sourceBars) {
        const bucketTime = Math.floor(entry.time / intervalMs) * intervalMs;

        if (!bucket || bucket.time !== bucketTime) {
          if (bucket) resultBars.push(bucket);
          bucket = {
            time: bucketTime,
            open: Number(entry.open),
            high: Number(entry.high),
            low: Number(entry.low),
            close: Number(entry.close)
          };
        } else {
          bucket.high = Math.max(bucket.high, entry.high);
          bucket.low = Math.min(bucket.low, entry.low);
          bucket.close = Number(entry.close);
        }
      }

      if (bucket) resultBars.push(bucket);

      const returnedBars = applyRequestedRange(resultBars, intervalMs);
      this.lastBars[cleanSymbol] = returnedBars[returnedBars.length - 1];
      onHistoryCallback(returnedBars, {noData: false});
    } catch (error) {
      onErrorCallback(error.message);
    }
  }

  subscribeBars(symbolInfo, resolution, onRealtimeCallback, subscriberUID, onResetCacheNeededCallback) {
    if (this.destroyed) return;

    const cleanSymbol = symbolInfo.ticker.replace('I:', '');
    const interval = parseInt(resolution, 10);
    const intervalMs = interval * 60 * 1000;

    if (this.subscribers?.[subscriberUID]) {
      this.unsubscribeBars(subscriberUID);
    }

    if (!this.subscribers) this.subscribers = {};
    if (!this.storeUnsubscribers) this.storeUnsubscribers = {};
    if (!this.playbackUnsubscribers) this.playbackUnsubscribers = {};
    if (!this.aggBuckets) this.aggBuckets = {};  // for storing partial bars

    this.subscribers[subscriberUID] = onRealtimeCallback;

    const deliverLiveBar = (state) => {
      const oneMinuteBar = state.aggregationState[cleanSymbol];

      if (!oneMinuteBar || !oneMinuteBar.startTime) return;

      const barTime = Number(oneMinuteBar.startTime);
      if (isNaN(barTime)) return;

      const bucketTime = Math.floor(barTime / intervalMs) * intervalMs;

      if (!this.aggBuckets[subscriberUID]) {
        this.aggBuckets[subscriberUID] = {
          time: bucketTime,
          open: Number(oneMinuteBar.open),
          high: Number(oneMinuteBar.high),
          low: Number(oneMinuteBar.low),
          close: Number(oneMinuteBar.close)
        };
      }

      const bucket = this.aggBuckets[subscriberUID];

      if (bucketTime !== bucket.time) {
        // Emit the completed bar for the previous interval
        onRealtimeCallback({ ...bucket });

        // Start a new bucket
        this.aggBuckets[subscriberUID] = {
          time: bucketTime,
          open: Number(oneMinuteBar.open),
          high: Number(oneMinuteBar.high),
          low: Number(oneMinuteBar.low),
          close: Number(oneMinuteBar.close)
        };

        // Also emit the new (just started) bucket as the current in-progress bar
        onRealtimeCallback({ ...this.aggBuckets[subscriberUID] });
      } else {
        // Update the current bucket
        bucket.high = Math.max(bucket.high, oneMinuteBar.high);
        bucket.low = Math.min(bucket.low, oneMinuteBar.low);
        bucket.close = Number(oneMinuteBar.close);

        // Emit the in-progress bar every update!
        onRealtimeCallback({ ...bucket });
      }
    };

    const unsubscribeLive = () => {
      this.storeUnsubscribers[subscriberUID]?.();
      delete this.storeUnsubscribers[subscriberUID];
    };

    const subscribeLive = () => {
      if (this.storeUnsubscribers[subscriberUID]) return;
      this.storeUnsubscribers[subscriberUID] = useWebSocketDataStore.subscribe(deliverLiveBar);
    };

    const resetSubscription = () => {
      delete this.aggBuckets[subscriberUID];
      try {
        onResetCacheNeededCallback?.();
      } catch (error) {
        console.warn('[CustomDataFeed] Replay cache reset failed:', error);
      }
    };

    this.playbackUnsubscribers[subscriberUID] = usePlaybackStore.subscribe((state, previousState) => {
      const replayChanged = state.mode === 'playing'
        ? previousState.mode !== 'playing'
          || state.replayCutoffMs !== previousState.replayCutoffMs
        : previousState.mode === 'playing';
      if (!replayChanged) return;

      resetSubscription();
      if (state.mode === 'playing') unsubscribeLive();
      else subscribeLive();
    });

    if (usePlaybackStore.getState().mode !== 'playing') subscribeLive();
  }

  // Existing unsubscribeBars method remains unchanged
  unsubscribeBars(subscriberUID) {
    if (this.storeUnsubscribers?.[subscriberUID]) {
      this.storeUnsubscribers[subscriberUID](); // call unsubscribe function
      delete this.storeUnsubscribers[subscriberUID];
    }
    if (this.playbackUnsubscribers?.[subscriberUID]) {
      this.playbackUnsubscribers[subscriberUID]();
      delete this.playbackUnsubscribers[subscriberUID];
    }
    if (this.subscribers?.[subscriberUID]) {
      delete this.subscribers[subscriberUID];
    }
    if (this.aggBuckets?.[subscriberUID]) {
      delete this.aggBuckets[subscriberUID];
    }
  }

  destroy() {
    this.destroyed = true;
    const subscriberUIDs = new Set([
      ...Object.keys(this.subscribers || {}),
      ...Object.keys(this.storeUnsubscribers || {}),
      ...Object.keys(this.playbackUnsubscribers || {}),
    ]);
    for (const subscriberUID of subscriberUIDs) {
      this.unsubscribeBars(subscriberUID);
    }
  }
}
export default CustomDataFeed;




















